import { parse } from "tldts"
import { z } from "zod"

import type { Email, Prisma } from "@productlane/db"
import { commonEmailProviders } from "@productlane/lib"
import { isValidEmailAddress } from "@productlane/lib/src/email-domain"
import { formatEmailHtmlContent } from "@productlane/lib/src/html"

import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "./constants"

/**
 * Fields to merge into a Prisma Email create so the heavy body is mirrored
 * into the EmailContent sidecar (what clients sync for the detail view) and a
 * short list preview is stored on the Email row.
 */
export const withEmailContent = (input: {
	htmlContent: string
	textContent: string
	attachments?: Prisma.InputJsonValue[]
	workspaceId: string
}): Pick<Prisma.EmailCreateInput, "preview" | "content"> => ({
	preview: formatEmailHtmlContent(input.htmlContent || input.textContent || ""),
	content: {
		create: {
			htmlContent: input.htmlContent,
			textContent: input.textContent,
			attachments: input.attachments ?? [],
			workspace: { connect: { id: input.workspaceId } },
		},
	},
})

export const EmailContactSchema = z.object({
	name: z.string(),
	email: z
		.string()
		.max(EMAIL_MAXIMUM_LENGTH, EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE),
	mailboxHash: z.string().optional().default(""),
})
// For addresses a caller hands us to send to. Stored rows keep the loose schema.
export const OutboundEmailContactSchema = EmailContactSchema.extend({
	email: EmailContactSchema.shape.email.refine(isValidEmailAddress, {
		message: "Not a valid email address",
	}),
})
export const EmailAttachmentSchema = z.object({
	Content: z.string().optional(),
	ContentLength: z.string().or(z.number()).optional().nullable(),
	Name: z.string(),
	ContentType: z.string(),
	ContentID: z.string().optional(),
})

export type EmailContact = z.infer<typeof EmailContactSchema>
export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>

/**
 * The inbound payload as stored on Email.raw. Uploaded attachment bodies live as
 * URLs on `attachments`; raw keeps the base64 only for uploads that failed.
 */
export const withoutAttachmentContent = <
	T extends { Attachments: EmailAttachment[] },
>(
	payload: T,
	failedUploads: ReadonlySet<EmailAttachment> = new Set(),
) => ({
	...payload,
	Attachments: payload.Attachments.map((attachment) => {
		if (failedUploads.has(attachment)) return attachment
		const { Content: _content, ...meta } = attachment
		return meta
	}),
})

export const populateEmailAttachments =
	(attachments: EmailAttachment[]) => (htmlContent: Email["htmlContent"]) =>
		htmlContent.replace(/src="cid:([^"]+)"/g, (_, p1: string) => {
			const attachment = attachments.find((a) => {
				if (!a.ContentID) return false
				const normalizedContentId = a.ContentID.replace(/^<|>$/g, "")
				return (
					normalizedContentId.includes(p1) || p1.includes(normalizedContentId)
				)
			})

			if (!attachment?.Content) return 'src=""'

			// Only URL-backed content is rendered; legacy base64 blobs are dropped
			// so no multi-MB data: image is ever built from a synced email row.
			if (
				attachment.Content.startsWith("http://") ||
				attachment.Content.startsWith("https://")
			) {
				return `src="${attachment.Content}"`
			}

			return 'src=""'
		})

/**
 * Extracts Content IDs (CIDs) from HTML content.
 * Used to identify which attachments are embedded in a particular HTML section.
 */
export const extractCidsFromHtml = (htmlContent: string): string[] => {
	const cids: string[] = []
	const cidRegex = /src="cid:([^"]+)"/g
	let match
	while ((match = cidRegex.exec(htmlContent)) !== null) {
		if (match[1]) {
			cids.push(match[1])
		}
	}
	return cids
}

/**
 * Filters out attachments that are used in signature/hidden content.
 * Returns attachments that are NOT in the hidden portion of the email.
 */
export const filterSignatureAttachments = (
	attachments: EmailAttachment[],
	hiddenContent: string,
): EmailAttachment[] => {
	if (!hiddenContent) return attachments

	const signatureCids = extractCidsFromHtml(hiddenContent)
	if (signatureCids.length === 0) return attachments

	return attachments.filter(
		(attachment) =>
			!signatureCids.some((cid) => attachment.ContentID?.includes(cid)),
	)
}

export type InboundMailClass = "HUMAN" | "AUTOMATED" | "AUTO_REPLY" | "SPAM"

export interface InboundMailClassification {
	class: InboundMailClass
	reason?: string
}

interface MailHeader {
	Name: string
	Value: string
}

// Unambiguous vacation-responder markers. Nothing but an auto-responder sets these.
const VACATION_FLAG_HEADERS = [
	"X-Autoreply",
	"X-Autorespond",
	"X-amzn-vacation",
	"X-Vacationmessage",
]

// Machine-generated markers that receipts, lead notifications and rule-forwarded mail
// also set, so on their own they only mean "not typed by a person right now".
const MACHINE_MARKER_HEADERS = [
	"X-MS-Exchange-Inbox-Rules-Loop",
	"X-Auto-Response-Suppress",
]

// Localized auto-reply subjects, anchored to the start so "Frage zum Urlaubsmodul"
// stays a human email. Optional reply/forward prefixes come first because each
// round trip prepends another one.
// Outlook reaction notifications ("X reacted to your message") come from the
// reactor's own mailbox with no auto-reply header; the embedded reaction glyph
// (e.g. outlook-1.cdn.office.net/assets/reaction/heart.png) is the only
// language-independent marker.
const OUTLOOK_REACTION_GLYPH =
	/https:\/\/[^"'\s>]*\.cdn\.office\.net\/assets\/reaction\//i

const AUTO_REPLY_SUBJECT =
	/^\s*(?:(?:re|aw|antw|antwort|fw|fwd|wg|tr|res|sv|vs|encaminhado)\s*:\s*)*(?:automatic(?:al)? reply|auto reply|autoreply|automatische antwort|abwesenheit|abwesenheitsnotiz|abwesenheitsmitteilung|out of (?:the )?office|r[eé]ponse automatique|accus[eé] (?:de )?r[eé]ception|risposta automatica|respuesta autom[aá]tica|resposta autom[aá]tica|aus[eê]ncia|automatisch antwoord|automatyczna odpowied[zź]|automaattinen vastaus|automatisk svar|autosvar|fr[aå]nvaro|自動応答|自动回复|자동 회신)/i

/**
 * Classifies an inbound email as human, machine-generated, or an auto-reply.
 *
 * AUTO_REPLY is filed out of the inbox and never answered. AUTOMATED and SPAM stay
 * in the inbox but are never answered, which covers receipts and lead notifications
 * that a workspace does want to see, and keeps a false spam verdict recoverable. Header markers alone are not enough for AUTO_REPLY:
 * `Auto-Submitted: auto-generated`, `X-Auto-Response-Suppress` and Exchange's inbox
 * rule header all appear on genuine mail forwarded by an Outlook rule, so they must
 * be paired with a localized auto-reply subject or with the message being a reply to
 * something we sent. A List-Unsubscribe / List-Id header marks a mailing list, which
 * is never an auto-reply, and is AUTOMATED once it also carries a bulk marker.
 */
export const classifyInboundMail = ({
	headers,
	subject,
	htmlBody,
	repliesToOwnOutboundMail = false,
}: {
	headers: MailHeader[]
	subject?: string | null
	htmlBody?: string | null
	repliesToOwnOutboundMail?: boolean
}): InboundMailClassification => {
	const get = (name: string): string | undefined =>
		headers.find((h) => h.Name.toLowerCase() === name.toLowerCase())?.Value
	const value = (name: string) => get(name)?.trim().toLowerCase()
	const describe = (name: string) => `${name}: ${get(name)?.trim()}`

	// SpamAssassin's verdict, set by Postmark on every inbound message. It comes
	// before the mailing-list check, since bulk spam sets List-Unsubscribe too.
	const spamStatus = value("X-Spam-Status")
	if (value("X-Spam-Flag") === "yes")
		return { class: "SPAM", reason: describe("X-Spam-Flag") }
	if (spamStatus?.startsWith("yes"))
		return { class: "SPAM", reason: describe("X-Spam-Status") }

	// Google Groups relays a person's mail with List-Id and Precedence: list, so only
	// a newsletter's one-click unsubscribe, bulk precedence or Auto-Submitted count.
	if (get("List-Unsubscribe") || get("List-Id")) {
		const listPrecedence = value("Precedence")
		const bulkMarker =
			(get("List-Unsubscribe-Post") !== undefined
				? "List-Unsubscribe-Post"
				: undefined) ??
			(listPrecedence === "bulk" || listPrecedence === "junk"
				? "Precedence"
				: undefined) ??
			(value("Auto-Submitted")?.startsWith("auto-generated")
				? "Auto-Submitted"
				: undefined)
		return bulkMarker
			? { class: "AUTOMATED", reason: describe(bulkMarker) }
			: { class: "HUMAN" }
	}

	if (htmlBody && OUTLOOK_REACTION_GLYPH.test(htmlBody))
		return { class: "AUTO_REPLY", reason: "Outlook reaction glyph in body" }

	const autoSubmitted = value("Auto-Submitted")
	if (autoSubmitted === "auto-replied" || autoSubmitted === "auto-notified")
		return { class: "AUTO_REPLY", reason: describe("Auto-Submitted") }

	for (const name of VACATION_FLAG_HEADERS) {
		if ((value(name) ?? "no") !== "no")
			return { class: "AUTO_REPLY", reason: describe(name) }
	}

	if (value("X-Zarafa-Rule-Action") === "reply")
		return { class: "AUTO_REPLY", reason: describe("X-Zarafa-Rule-Action") }

	const precedence = value("Precedence")
	if (precedence === "auto_reply")
		return { class: "AUTO_REPLY", reason: describe("Precedence") }

	const returnPath = value("Return-Path")
	const nullEnvelope =
		returnPath === "<>" || (returnPath?.includes("mailer-daemon") ?? false)

	const marker =
		MACHINE_MARKER_HEADERS.find((name) => get(name) !== undefined) ??
		(autoSubmitted?.startsWith("auto-generated")
			? "Auto-Submitted"
			: undefined) ??
		(precedence === "bulk" || precedence === "junk"
			? "Precedence"
			: undefined) ??
		(nullEnvelope ? "Return-Path" : undefined)

	if (!marker) return { class: "HUMAN" }

	if (AUTO_REPLY_SUBJECT.test(subject ?? ""))
		return {
			class: "AUTO_REPLY",
			reason: `${describe(marker)} + auto-reply subject`,
		}

	if (repliesToOwnOutboundMail)
		return {
			class: "AUTO_REPLY",
			reason: `${describe(marker)} + reply to our own mail`,
		}

	return { class: "AUTOMATED", reason: describe(marker) }
}

const AUTOMATED_SENDER_LOCAL_PARTS =
	/^(no-?reply|do-?not-?reply|notifications?|notify|mailer-daemon|postmaster|bounces?|auto-?reply|alerts?)([+._-].*)?$/i

// Google Groups rewrites forwarded senders to "Original Name via Group Name",
// so a "via" display name means the address is a group, not the person.
export const isAutomatedSender = (
	name: string | undefined,
	email: string,
): boolean => {
	if (name && /\svia\s/i.test(name)) return true
	const localPart = email.split("@")[0] ?? ""
	return AUTOMATED_SENDER_LOCAL_PARTS.test(localPart)
}

// A Google Group, or a mailbox relaying mail as the workspace's own address,
// keeps the person who wrote it only in Reply-To.
export function relayedSender({
	fromEmail,
	fromName,
	replyTo,
	selfEmails,
}: {
	fromEmail: string
	fromName: string | undefined
	replyTo: string | undefined
	selfEmails: readonly string[]
}): { email: string; name: string } | null {
	const isSelf = (email: string) =>
		selfEmails.includes(email.trim().toLowerCase())
	const isRelay = isSelf(fromEmail) || (!!fromName && /\svia\s/i.test(fromName))
	if (!isRelay || !replyTo) return null

	for (const part of replyTo.split(",")) {
		const match = /^(.*)<([^>]+)>\s*$/.exec(part.trim())
		const email = (match?.[2] ?? part).trim().toLowerCase()
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || isSelf(email)) continue
		const name = match?.[1]?.trim().replace(/^"|"$/g, "").trim()
		return { email, name: name || email }
	}
	return null
}

/**
 * A thread's contact is verified only when it IS the DKIM-authenticated From.
 * A Reply-To, forwarded, or relayed ("Name via Group") address borrows the
 * carrier mail's DKIM but is not itself proven, so putting a victim's address
 * there must never mark them verified (PRO-11229 H1).
 */
export function isInboundContactVerified({
	authenticated,
	contactEmail,
	fromEmail,
}: {
	authenticated: boolean
	contactEmail: string
	fromEmail: string
}): boolean {
	return (
		authenticated &&
		contactEmail.trim().toLowerCase() === fromEmail.trim().toLowerCase()
	)
}

/**
 * The customer of a mail the team sent outside Productlane, from a workspace
 * address or a teammate's own mailbox, that reached the inbox by cc, bcc or a
 * forwarding group: the first recipient that is not the workspace itself.
 */
export function ownMailRecipient({
	fromEmail,
	to,
	cc,
	selfEmails,
	memberEmails = [],
}: {
	fromEmail: string
	to: readonly { Email: string; Name?: string }[]
	cc: readonly { Email: string; Name?: string }[]
	selfEmails: readonly string[]
	memberEmails?: readonly string[]
}): { email: string; name: string } | null {
	const isOwn = (email: string) => {
		const normalized = email.trim().toLowerCase()
		return selfEmails.includes(normalized) || memberEmails.includes(normalized)
	}
	if (!isOwn(fromEmail)) return null

	for (const recipient of [...to, ...cc]) {
		const email = recipient.Email.trim().toLowerCase()
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || isOwn(email)) continue
		if (email.endsWith(".productlane.com")) continue
		return { email, name: recipient.Name?.trim() || email }
	}
	return null
}

const OWN_NOTIFICATION_MAIL_DOMAIN = "notifications.productlane.com"

// A member mailbox that auto-forwards into its own workspace inbound address turns
// every notification we send into a new thread, which sends another notification.
export const isOwnNotificationSender = (email: string): boolean => {
	const host = email.split("@")[1]?.trim().toLowerCase().replace(/\.$/, "")
	if (!host) return false
	return (
		host === OWN_NOTIFICATION_MAIL_DOMAIN ||
		host.endsWith(`.${OWN_NOTIFICATION_MAIL_DOMAIN}`)
	)
}

const OWN_MAIL_HEADER_NAMES = new Set([
	"return-path",
	"sender",
	"reply-to",
	"x-original-from",
	"x-original-sender",
	"x-forwarded-for",
	"x-forwarded-to",
])

// Any address inside a header value, so "Name <a@b.com>" and a bare address
// both resolve.
const addressesIn = (value: string): string[] =>
	value.match(/[^\s<>,;"']+@[^\s<>,;"']+/g) ?? []

/**
 * True when an inbound mail is a copy of one we sent, however it was forwarded.
 * A forwarder can rewrite the visible sender, so every address-bearing header
 * counts, not just From.
 */
export const isOwnNotificationMail = (input: {
	fromEmail: string
	headers?: readonly { Name: string; Value: string }[] | null
}): boolean => {
	if (isOwnNotificationSender(input.fromEmail)) return true

	for (const header of input.headers ?? []) {
		if (!OWN_MAIL_HEADER_NAMES.has(header.Name.trim().toLowerCase())) continue
		if (addressesIn(header.Value).some(isOwnNotificationSender)) return true
	}
	return false
}

const registrableDomain = (email: string | null | undefined): string | null => {
	const host = email?.split("@")[1]?.trim().toLowerCase()
	if (!host) return null
	return parse(host).domain ?? host
}

// A teammate answering from their own mailbox arrives as inbound mail whenever
// the support address sits on the thread, so their reply must never take the
// thread over from the customer who opened it.
export const isOwnDomainSender = ({
	senderEmail,
	supportEmail,
}: {
	senderEmail: string
	supportEmail: string | null | undefined
}): boolean => {
	const supportDomain = registrableDomain(supportEmail)
	if (!supportDomain || commonEmailProviders.includes(supportDomain)) {
		return false
	}
	return registrableDomain(senderEmail) === supportDomain
}

// Converts email participant objects into searchable names and addresses.
export function participantText(value: unknown): string {
	if (!value || typeof value !== "object") return ""
	if (Array.isArray(value))
		return value.map(participantText).filter(Boolean).join(" ")

	const participant = value as Record<string, unknown>
	return [participant.name, participant.email, participant.address]
		.filter((part): part is string => typeof part === "string")
		.join(" ")
}

export { formatEmailHtmlContent } from "@productlane/lib/src/html"
